<script lang="ts">
  import { selectedOrgStore } from "$lib/stores/orgs";
  import SectionTitle from "$lib/components/shared/SectionTitle.svelte";
  import Button from "$lib/ui/button/button.svelte";
  import { CircleAlert } from "lucide-svelte";
  import { push } from "svelte-spa-router";
  import Lock from "$assets/images/lock.png";
  import DarkData from "$assets/images/dark_data.png";
  import GoverningStructuredData from "$assets/images/governing_structured_data.png";
  import HowMuchOfYourSensitiveDataIsOverPermissioned from "$assets/images/how_much_of_your_sensitive_data_is_over-permissioned.png";
  import PageAccessDenied from "../PageAccessDenied.svelte";

  $: org = $selectedOrgStore;
  const orgSetup = true;
  const isLoading = false;

  const setupInstructions = [
    {
      title: "Define your data security implementation team",
      description:
        "Permissions Assurance works with you to ensure your report runs smoothly. Along the way we will need access to your enterprise's LDAP provider and then the correct permissions to authorise an assessment against your knowledge bases.",
    },
    {
      title: "Authorize your LDAP provider",
      description: "Connect to your organisation's Single Sign On (SSO) provider.",
    },
    {
      title: "Connect as an admin to your knowledge base",
      description: "Microsot Entra, Confluence, Slack, Google Drive...",
    },
    {
      title: "Wait for your assessment to complete and begin remediating data leaks and misconfigurations",
      description:
        "Permissions Assurance will provide you with a report of all the data leaks and misconfigurations found in your knowledge base.",
    },
  ];

  const detectedIssues = [
    {
      source: ["Microsoft 365", "Google Drive", "Slack"],
      id: "88",
      title: "Documents with the same semantic content have differing permissions",
      description: "An unauthorized user attempts to access a restricted document multiple times.",
      involves: ["user1", "user2", "user3"],
      date: new Date(2024, 9, 14, 12, 20, 0), // 14 Oct, 2024 12:20pm
      action: "See in graph",
      severity: "Critical",
      type: "Data Leak",
      sources: ["Doc1"],
      recommendation: "Investigate the permissions of the documents and ensure they are set correctly.",
    },
    {
      source: ["Microsoft 365", "Google Drive"],
      id: "97",
      title: "A user has access to an unusually large number of documents",
      description: "A user accesses documents outside their usual scope or during unusual hours.",
      involves: ["user2", "user3"],
      date: new Date(2024, 9, 14, 12, 0, 0), // 14 Oct, 2024 12:00pm
      action: "Investigate",
      severity: "High",
      type: "Misconfigured Permissions",
      sources: ["Doc2", "Doc3"],
      recommendation: "Investigate the permissions of the documents and ensure they are set correctly.",
    },
    {
      source: ["Google Drive"],
      id: "96",
      title: "Documents with the same semantic content have differing permissions",
      description: "An unauthorized user attempts to access a restricted document multiple times.",
      involves: ["user3"],
      date: new Date(2024, 9, 14, 11, 53, 0), // 14 Oct, 2024 11:53pm
      action: "Investigate",
      severity: "High",
      type: "Data Leak",
      sources: ["Doc4"],
      recommendation: "Investigate the permissions of the documents and ensure they are set correctly.",
    },
    {
      source: ["Microsoft 365", "Slack"],
      id: "95",
      title: "Documents with the same semantic content have differing permissions",
      description: "An unauthorized user attempts to access a restricted document multiple times.",
      involves: ["user4"],
      date: new Date(2024, 9, 14, 11, 3, 0), // 14 Oct, 2024 11:03am
      action: "Investigate",
      severity: "Medium",
      type: "Data Leak",
      sources: ["Doc5", "Doc6"],
      recommendation: "Investigate the permissions of the documents and ensure they are set correctly.",
    },
  ];

  const org_config = {
    sso_provider: "Okta",
    data_sources: ["Microsoft 365", "Google Drive", "Slack"],
  };

  const recent_blogs = [
    {
      title: "What is dark data? Understanding the business risks on unknown data estates",
      description:
        "Dark data creates escalating compliance, security, and operational risks. It also undermines AI adoption. Reducing data sprawl with centralized visibility is essential to discover unknown data and accelerate AI programs.",
      image: DarkData,
      link: "https://www.recordpoint.com/blog/what-is-dark-data",
      labels: ["Risk"],
    },
    {
      title: "Governing structured data: Retention, disposal and compliance across every database and warehouse",
      description:
        "Your most sensitive records don't just live in documents and email. They live in Snowflake, Oracle, SQL Server and dozens of other structured systems — and most governance tools never reach them. The RecordPoint Structured Data Connector changes that.",
      image: GoverningStructuredData,
      link: "https://www.recordpoint.com/blog/governing-structured-data",
      labels: ["Article", "Security"],
    },
    {
      title: "How Much of Your Sensitive Data Is Over-Permissioned?",
      description:
        "The rise of generative AI has transformed over-permissioning from a latent risk to one deserving urgent attention. But what's the scale of the issue? We attempted to find out",
      image: HowMuchOfYourSensitiveDataIsOverPermissioned,
      link: "https://www.recordpoint.com/blog/benchmark-how-much-of-your-sensitive-data-is-over-permissioned",
      labels: ["Security"],
    },
  ];
</script>

{#snippet organization_setup()}
  <div class="flex-1 flex flex-col gap-16">
    {#if $selectedOrgStore?.onboarding}
      <div class="bg-[#FFE2D4] rounded-sm p-6 text-offblack relative">
        <h2 class="font-semibold text-xl mb-2">Scan your organisation</h2>
        <p class="text-offblack">Understand the weaknesses and strengths of your company's security.</p>

        <div class="mt-5 p-4 border border-[#FFC1A8] min-w-[500px] w-[calc(100%-232px)] rounded-md relative">
          <p class="text-offblack flex gap-1 items-center mb-3">
            <CircleAlert />
            <span class="text-offblack">To scan your organisation, you must first set up your permissions</span>
          </p>
          <Button
            class="bg-orange-600 rounded-md"
            on:click={() =>
              push(
                $selectedOrgStore.id
                  ? `${$selectedOrgStore.pagePrefix}/connect_data`
                  : `${$selectedOrgStore.pagePrefix}/onboarding`,
              )}>Set up your security onboarding</Button
          >
        </div>

        <img src={Lock} alt="lock" class="absolute -bottom-20 h-[284px] -right-10" />
      </div>
    {/if}

    <div>
      <h2 class="text-[#181d27] text-2xl font-medium font-['Switzer Variable'] leading-loose mb-7">
        Recent blog posts
      </h2>
      <div class="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
        {#each recent_blogs as blog}
          <div class="flex flex-col">
            <img src={blog.image} alt={blog.title} class="w-full object-cover rounded-md" />
            <div class="mt-4">
              <h3 class="font-semibold hover:underline text-lg mb-3">
                <a href={blog.link}>
                  {blog.title}
                </a>
              </h3>
              <p class="text-offblack line-clamp-2">{blog.description}</p>
              <div class="flex gap-2 mt-3">
                {#each blog.labels as label}
                  <span class="bg-lime-200 text-lime-700 border border-lime-400 rounded-full px-2 py-1">{label}</span>
                {/each}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>
{/snippet}

<div class="grow px-6 lg:px-10 py-5 sm:py-8 lg:py-10 text-offblack">
  <SectionTitle>Overview</SectionTitle>
  {#if org}
    {@render organization_setup()}
  {:else}
    <PageAccessDenied />
  {/if}
</div>
